import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, MailCheck, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

export default function ForgotPassword() {
  useSeo("Passwort vergessen – LAIzyDocu", "Setzen Sie Ihr LAIzyDocu-Passwort zurück.");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anfrage fehlgeschlagen."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50 grid place-items-center px-6 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10">
        <Link to="/login" className="inline-flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-900 text-white grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
          <span className="font-display text-lg font-semibold text-emerald-900">LA<span className="text-emerald-600">I</span>zyDoc</span>
        </Link>

        {sent ? (
          <div data-testid="forgot-sent" className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center mx-auto mb-6"><MailCheck className="w-8 h-8" /></div>
            <h1 className="font-display text-2xl font-semibold text-emerald-900">E-Mail unterwegs</h1>
            <p className="text-stone-600 mt-3">Falls ein Konto mit dieser Adresse existiert, haben wir Ihnen einen Link zum Zurücksetzen gesendet. Der Link ist 1 Stunde gültig.</p>
            <Link to="/login" data-testid="forgot-back-login" className="inline-block mt-6">
              <Button className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">Zur Anmeldung</Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-form">
            <h1 className="font-display text-3xl font-semibold text-emerald-900">Passwort vergessen?</h1>
            <p className="text-stone-500 mt-2 mb-7">Geben Sie Ihre E-Mail-Adresse ein – wir senden Ihnen einen Link zum Zurücksetzen.</p>
            <Label htmlFor="forgot-email" className="text-base font-medium text-stone-800">E-Mail-Adresse</Label>
            <div className="relative mt-2">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input id="forgot-email" data-testid="forgot-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                className="h-14 pl-12 pr-4 text-lg rounded-xl" placeholder="praxis@beispiel.de" />
            </div>
            <Button type="submit" data-testid="forgot-submit" disabled={loading}
              className="w-full h-16 mt-7 text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
              {loading ? "Wird gesendet…" : "Link zum Zurücksetzen senden"}
            </Button>
            <Link to="/login" data-testid="forgot-back" className="mt-6 flex items-center justify-center gap-1 text-sm text-stone-500 hover:text-stone-700">
              <ArrowLeft className="w-4 h-4" /> Zurück zur Anmeldung
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
